import { ErrorPage } from './ErrorPage';

interface RenderErrorPageProps {
  reason?: string;
  /** Optional callback for a "Try again" action (defaults to window.reload). */
  onRetry?: () => void;
}

/**
 * Shown when the React tree throws during render — a bug in *our client code*,
 * not a server failure.
 *
 * This exists because `ErrorBoundary` used to render `ServerErrorPage` for these,
 * announcing "500 · Server Error / Something went wrong on our end" for what was
 * actually a local TypeError. That sends anyone debugging it — user or engineer —
 * straight to the backend logs, where there is nothing to find. Keep the two
 * distinct: `ServerErrorPage` for real 5xx responses, this for render crashes.
 */
export function RenderErrorPage({ reason, onRetry }: RenderErrorPageProps = {}) {
  return (
    <ErrorPage
      code="Display Error"
      title="This page couldn't be displayed"
      description="Something went wrong rendering this screen. Your data is safe and unaffected — reloading usually clears it. If it keeps happening, send the detail below to support; it identifies the bug."
      tone="destructive"
      {...(reason ? { details: reason } : {})}
      primaryAction={{
        label: 'Try again',
        onClick: () => {
          if (onRetry) onRetry();
          else window.location.reload();
        },
      }}
      secondaryAction={{
        label: 'Reload page',
        onClick: () => window.location.reload(),
      }}
    />
  );
}
